'use client';

import { useEffect, useRef, useState } from 'react';
import type { ContentfulInlineFrameType } from 'lib/contentful/types';

type OpenTableIframeProps = Readonly<{
  fields: ContentfulInlineFrameType;
}>;

function getOpenTableLoaderUrl(url: string) {
  try {
    const parsedUrl = new URL(url);

    if (
      parsedUrl.hostname.includes('opentable.com') &&
      parsedUrl.pathname.includes('/widget/reservation/loader')
    ) {
      parsedUrl.searchParams.set('iframe', 'false');
    }

    return parsedUrl.toString();
  } catch {
    return url;
  }
}

const isValidScriptUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

function OpenTableInlineScript({ fields }: OpenTableIframeProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const embedUrl = fields.url ? getOpenTableLoaderUrl(fields.url) : fields.url;

  useEffect(() => {
    const onScroll = () => {
      setHasScrolled(true);
    };

    window.addEventListener('scroll', onScroll, { once: true, passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' },
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !shouldLoad || !hasScrolled || !embedUrl || !isValidScriptUrl(embedUrl))
      return;

    const existingContainerScript = container.querySelector('script[data-opentable-loader="true"]');
    if (existingContainerScript) return;

    const script = document.createElement('script');
    script.dataset.opentableLoader = 'true';
    script.src = embedUrl;
    script.type = 'text/javascript';
    script.async = true;
    container.appendChild(script);

    return () => {
      script.remove();
    };
  }, [embedUrl, shouldLoad, hasScrolled]);

  return (
    <section
      ref={containerRef}
      aria-label={fields.title}
      className="flex w-full justify-center overflow-x-auto [&>div]:mx-auto [&_iframe]:mx-auto [&_iframe]:block"
    />
  );
}

export default function OpenTableIframe({ fields }: OpenTableIframeProps) {
  if (fields.type === 'script') {
    return <OpenTableInlineScript fields={fields} />;
  }

  return (
    <iframe
      allow="geolocation"
      aria-label={fields.title}
      src={fields.url}
      title={fields.title}
      width="100%"
    />
  );
}
